import React, { FC, useCallback, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, notification, Select, useLanguage, useMe } from 'src/common';
import AvatarSettings from '../AvatarSettings';
import { useUpdateLanguage, useUpdateUser } from 'src/models/users/Account/hooks';
import { Language } from '@tendium/prom-types';
import { useApp, useFreeUser, UserType } from 'src/models/auth';
import { Auth } from 'aws-amplify';

interface FormState {
  name: string;
  lastName: string;
  language: Language;
}
export const UserSettings: FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormState>();
  const [touched, setTouched] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { authUser, permission } = useApp();
  const { data: paidUser } = useMe();
  const [rawFreeUser, updateFreeUser] = useFreeUser();
  const isPaid = useMemo(() => permission === UserType.Paid && !!authUser, [authUser, permission]);
  const email = useMemo(() => (paidUser ? paidUser.email : undefined), [paidUser]);
  const [updatePaidUser] = useUpdateUser();
  const [updatePaidLanguage] = useUpdateLanguage();
  const { language } = useLanguage();

  const initialValues = useMemo(
    (): FormState => ({
      name: paidUser ? paidUser.name ?? '' : '',
      lastName: paidUser ? paidUser.lastName ?? '' : '',
      language
    }),
    [language, paidUser]
  );

  const onFinish = useCallback(
    (values: FormState) => {
      const { name, lastName } = values;
      setIsUpdating(() => true);
      if (isPaid) {
        email &&
          updatePaidUser({ variables: { email, name, lastName } })
            .then(() => {
              form.resetFields();
            })
            .catch(() => {
              notification.error({
                message: t('Common.unknownError'),
                description: t('Common.unknownErrorDesc')
              });
            })
            .finally(() => {
              setIsUpdating(() => false);
            });
      }
    },
    [email, form, isPaid, t, updatePaidUser]
  );

  const onChange = useCallback(
    (changedValues: Partial<FormState>) => {
      if (changedValues.language) {
        const { language } = changedValues;
        if (isPaid) {
          email &&
            updatePaidLanguage({ variables: { id: email, language } }).catch(() => {
              notification.error({
                message: t('Common.unknownError'),
                description: t('Common.unknownErrorDesc')
              });
            });
        } else {
          Auth.updateUserAttributes(authUser, { locale: language })
            .then(() => {
              rawFreeUser &&
                updateFreeUser({
                  ...rawFreeUser,
                  locale: language
                });
            })
            .catch(() => {
              notification.error({
                description: t('Common.unknownErrorDesc'),
                message: t('Common.unknownError')
              });
            });
        }
      } else if (
        (changedValues.name && changedValues.name !== initialValues.name) ||
        (changedValues.lastName && changedValues.lastName !== initialValues.lastName)
      ) {
        setTouched(true);
      } else {
        setTouched(false);
      }
    },
    [
      authUser,
      email,
      initialValues.lastName,
      initialValues.name,
      isPaid,
      rawFreeUser,
      t,
      updateFreeUser,
      updatePaidLanguage
    ]
  );

  return (
    <div className={styles.contentInner}>
      <h3 className={styles.contentTitle}>
        <span>{t('Common.profile')}</span>
      </h3>
      <p className={styles.contentDesc}>{t('Account.userFormDesc')}</p>
      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={onChange}
        initialValues={initialValues}
        className={styles.formContent}
        layout={'vertical'}
      >
        {isPaid && (
          <>
            <Form.Item name={'name'} label={t('Account.firstName')}>
              <Input placeholder={t('Account.firstName')} />
            </Form.Item>
            <Form.Item name={'lastName'} label={t('Account.lastName')}>
              <Input placeholder={t('Account.lastName')} />
            </Form.Item>

            <Form.Item label={t('Account.avatar')}>
              <AvatarSettings />
            </Form.Item>
          </>
        )}
        <Form.Item label={t('Account.language')} name={'language'}>
          <Select optionLabelProp={'label'} getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}>
            {Object.values(Language).map(lang => (
              <Select.Option value={lang} key={lang} label={`${t(`Common.Lang.${lang}`)}`}>
                {`${t(`Common.Lang.${lang}`)}`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {isPaid && (
          <div className={styles.formSubmit}>
            <Button type={'primary'} htmlType={'submit'} loading={isUpdating} disabled={!touched}>
              {t('Common.updateYourProfile')}
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default UserSettings;
